import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, of, forkJoin } from 'rxjs';
import { environment } from '@env/environment';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';

export interface SupersetSqlLabRequest {
  sql: string;
  database_id: number;
  schema?: string;
  client_id: string;
  queryLimit?: number;
  runAsync?: boolean;
  json?: boolean;
  expand_data?: boolean;
  ctas_method?: string;
  select_as_cta?: boolean;
  sql_editor_id?: string;
  tab?: string;
  tmp_table_name?: string;
}

export interface SupersetSqlLabResponse {
  id?: string;
  status?: string;
  progress?: number;
  results_key?: string;
  state?: string;
  data?: any | {
    data?: any[];
    columns?: Array<{
      name: string;
      type: string;
    }>;
  };
  error?: string;
  errors?: string[];
  columns?: Array<{
    name: string;
    type: string;
  }>;
  query?: {
    startDttm: number;
    endDttm: number;
    executedSql: string;
    sqlEditorId: string;
    schema?: string;
    // Query status fields
    queryId?: number;
    serverId?: number;
    id?: string;
    state?: string;
    status?: string;
    progress?: number;
    results_key?: string;
    sql?: string;
    db?: string;
    dbId?: number;
    rows?: number | null;
  };
  resultsKey?: string;   // Alternative property name for results_key
}

export interface SupersetDatabase {
  id: number;
  database_name: string;
  backend: string;
  allow_run_async: boolean;
  schemas?: string[];
  selected?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SupersetApiService {
  // Parse the Superset URL to get the base URL
  private baseUrl: string = this.getSupersetBaseUrl();
  private defaultDatabaseId = 1; // You might want to make this configurable or fetch it
  private supersetToken: string | null = null;
  private tokenExpirationTime: number = 0; // timestamp when token expires

  constructor(
    private http: HttpClient,
    private oidcSecurityService: OidcSecurityService
  ) { }

  /**
   * Execute SQL query via Superset SQL Lab API
   * @param sql The SQL query string to execute
   * @param databaseId The database ID to run the query against
   * @param schema Optional database schema
   * @param queryLimit Optional query result limit
   * @param runAsync Run asynchronously if true
   */
  executeSql(
    sql: string,
    databaseId: number = this.defaultDatabaseId,
    schema: string = '',
    queryLimit: number = 1000,
    runAsync: boolean = true // Changed default to true as SQL Lab uses that
  ): Observable<SupersetSqlLabResponse> {
    const url = `${this.baseUrl}/api/v1/sqllab/execute/`;
    // Generate a unique client ID for this SQL query execution
    const clientId = `${Math.random().toString(36).substring(2, 10)}`;

    // Create payload matching SQL Lab format
    const payload: SupersetSqlLabRequest = {
      sql,
      database_id: databaseId,
      schema,
      client_id: clientId,
      queryLimit,
      runAsync,
      json: true,
      expand_data: true,
      ctas_method: "TABLE",
    };

    // Convert the promise of headers to an observable, then use those headers for the POST request
    return from(this.getAuthHeaders()).pipe(
      switchMap(headers => {
        return this.http.post<SupersetSqlLabResponse>(url, payload, { headers })
          .pipe(
            catchError(error => {
              // If we get a 401 Unauthorized error, try to re-authenticate and retry once
              if (error.status === 401) {
                // Clear existing token to force re-authentication
                this.supersetToken = null;

                return from(this.accessLogin()).pipe(
                  switchMap(() => this.getAuthHeaders()),
                  switchMap(newHeaders => {
                    return this.http.post<SupersetSqlLabResponse>(url, payload, { headers: newHeaders });
                  })
                );
              }

              // Re-throw the error for other error types
              throw error;
            })
          );
      })
    );
  }

  // The getQueryResults method has been removed as it's been replaced by getQueryResultsByKey and getQueryResultsGet

  /**
   * Get SQL query results using resultsKey
   * @param resultsKey The results key to fetch results with
   */
  getQueryResultsByKey(resultsKey: string): Observable<SupersetSqlLabResponse> {
    if (!resultsKey) {
      return of({
        error: 'Invalid results key',
        status: 'failed'
      } as SupersetSqlLabResponse);
    }

    // Format the URL correctly with the proper rison format
    // Always use rows:100000 to ensure we get all results
    const risonParams = this.toRisonFormat({ key: resultsKey, rows: 100000 });
    const url = `${this.baseUrl}/api/v1/sqllab/results/?q=${encodeURIComponent(risonParams)}`;

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers => {
        return this.http.get<SupersetSqlLabResponse>(url, { headers })
          .pipe(
            catchError(error => {
              // If we get a 401 Unauthorized error, try to re-authenticate and retry once
              if (error.status === 401) {
                // Authentication error, clear token and retry
                this.supersetToken = null;

                return from(this.accessLogin()).pipe(
                  switchMap(() => this.getAuthHeaders()),
                  switchMap(newHeaders => {
                    return this.http.get<SupersetSqlLabResponse>(url, { headers: newHeaders });
                  })
                );
              }

              throw error;
            })
          );
      })
    );
  }

  /**
   * Get SQL query results for async queries using query ID
   * This method ensures the correct flow:
   * 1. First get query details to get results_key
   * 2. Then use results_key to get results
   * 
   * @param queryId The ID of the query to fetch results for
   * @param resultsKey Optional: The results key to use directly if already known
   */
  getQueryResultsGet(queryId: string, resultsKey?: string): Observable<SupersetSqlLabResponse> {
    // Check if we have a results key
    if (resultsKey) {
      // Always prefer using results_key if available
      return this.getQueryResultsByKey(resultsKey);
    }

    // If no results_key, we need to get the query details to obtain it
    // Ensure we have a valid query ID
    if (!queryId) {
      return of({
        error: 'Invalid query ID',
        status: 'failed'
      } as SupersetSqlLabResponse);
    }

    // First, get the query details
    return this.getQueryDetails(queryId).pipe(
      switchMap(queryDetails => {

        // Try to extract results_key from query details
        let extractedResultsKey = null;

        // Check if results_key exists directly in the response
        if (queryDetails.results_key) {
          extractedResultsKey = queryDetails.results_key;
        }
        // Check if it's nested under 'result'
        else if (queryDetails.result && queryDetails.result.results_key) {
          extractedResultsKey = queryDetails.result.results_key;
        }
        // Check for alternative property name
        else if (queryDetails.resultsKey) {
          extractedResultsKey = queryDetails.resultsKey;
        }
        // Check for nested alternative property name
        else if (queryDetails.result && queryDetails.result.resultsKey) {
          extractedResultsKey = queryDetails.result.resultsKey;
        }

        if (extractedResultsKey) {
          return this.getQueryResultsByKey(extractedResultsKey);
        } else {
          return of({
            error: 'No results_key found for this query. Cannot fetch results.',
            status: 'failed'
          } as SupersetSqlLabResponse);
        }
      }),
      catchError(error => {
        return of({
          error: `Failed to retrieve results: ${error.message || 'Unknown error'}`,
          status: 'failed'
        } as SupersetSqlLabResponse);
      })
    );
  }

  /**
   * Fetch available databases from Superset
   */
  getDatabases(): Observable<any> {
    const url = `${this.baseUrl}/api/v1/database/`;

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers => {
        return this.http.get<any>(url, { headers })
          .pipe(
            catchError(error => {
              // If we get a 401 Unauthorized error, try to re-authenticate and retry once
              if (error.status === 401) {
                // Clear existing token to force re-authentication
                this.supersetToken = null;

                return from(this.accessLogin()).pipe(
                  switchMap(() => this.getAuthHeaders()),
                  switchMap(newHeaders => {
                    return this.http.get<any>(url, { headers: newHeaders });
                  })
                );
              }

              // Re-throw the error for other error types
              throw error;
            })
          );
      })
    );
  }

  /**
   * Get detailed information about available databases with enhanced filtering
   * @param includeSchemas Whether to include schema information
   * @returns Observable of database information
   */
  getDatabasesDetailed(includeSchemas: boolean = false): Observable<SupersetDatabase[]> {
    // First get the list of databases
    return this.getDatabases().pipe(
      map(response => {
        // Parse database list - Superset typically returns { result: [...databases] }
        return (response.result || []) as SupersetDatabase[];
      }),
      switchMap(databases => {
        if (!includeSchemas || databases.length === 0) {
          return of(databases);
        }

        // Create an array of observables for each database schema request
        const observables = databases.map(db =>
          this.getDatabaseSchemas(db.id).pipe(
            map(schemas => ({
              ...db,
              schemas
            })),
            catchError(() => of({
              ...db,
              schemas: []
            }))
          )
        );

        // Use forkJoin to wait for all schema requests to complete
        return observables.length > 0 ? forkJoin(observables) : of(databases);
      }),
      map(databases => {
        // Mark the first database as selected by default
        if (databases && databases.length > 0) {
          return [{
            ...databases[0],
            selected: true
          }, ...databases.slice(1)];
        }
        return databases;
      })
    );
  }

  /**
   * Get schemas for a specific database
   * @param databaseId The database ID
   * @returns Observable of schema names
   */
  getDatabaseSchemas(databaseId: number): Observable<string[]> {
    const url = `${this.baseUrl}/api/v1/database/${databaseId}/schemas/`;

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.get<{ result: string[] }>(url, { headers }).pipe(
          map(response => response.result || []),
          catchError(error => {
            // Handle error fetching schemas

            // If unauthorized, try to refresh token
            if (error.status === 401) {
              return from(this.accessLogin()).pipe(
                switchMap(() => this.getAuthHeaders()),
                switchMap(newHeaders =>
                  this.http.get<{ result: string[] }>(url, { headers: newHeaders }).pipe(
                    map(response => response.result || [])
                  )
                )
              );
            }

            return of([]);
          })
        )
      )
    );
  }

  /**
   * Authenticate with Superset using XGS token
   * @param supersetUrl The URL of the Superset instance
   * @param xgsToken The XGS token to authenticate with
   * @returns Observable of the authentication response
   */
  auth(supersetUrl: string, xgsToken: string): Observable<any> {
    const payload = {
      provider: 'xgs',
      token: xgsToken,
      refresh: true
    };

    // Sending auth request to Superset
    return this.http.post<any>(supersetUrl + '/api/v1/security/login', payload);
  }

  /**
   * Login to Superset using XGS token
   * @param url The URL of the Superset instance
   * @returns Promise that resolves when login is complete
   */
  async accessLogin(url?: string): Promise<void> {
    const supersetUrl = url || this.baseUrl;
    
    return new Promise((resolve, reject) => {
      this.oidcSecurityService.getAccessToken().subscribe(async xgsToken => {
        if (xgsToken) {
          try {
            const authObservable = await this.auth(supersetUrl, xgsToken);
            const response = await new Promise<any>((resolveAuth, rejectAuth) => {
              authObservable.subscribe({
                next: (res) => {
                  this.supersetToken = res.access_token;

                  // Calculate token expiration time (default to 1 hour if not specified)
                  const expiresIn = res.expires_in || 3600;
                  this.tokenExpirationTime = Date.now() + (expiresIn * 1000);

                  // Token acquired and stored
                  resolveAuth(res);
                },
                error: (err) => {
                  rejectAuth(err);
                }
              });
            });
            resolve(response);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('No access token available'));
        }
      });
    });
  }

  /**
   * Check if the current token is valid
   * @returns True if the token is valid, false otherwise
   */
  isTokenValid(): boolean {
    // Check if we have a token and it's not expired
    return !!this.supersetToken && Date.now() < this.tokenExpirationTime;
  }

  /**
   * Get authentication headers for Superset API requests
   * @returns HttpHeaders with the authorization token
   */
  private async getAuthHeaders(): Promise<HttpHeaders> {
    // If token is not valid, try to get a new one
    if (!this.isTokenValid()) {
      await this.accessLogin();
    }

    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (this.supersetToken) {
      headers = headers.set('Authorization', `Bearer ${this.supersetToken}`);
    }

    return headers;
  }

  /**
   * Help extract the base URL from the Superset URL in environment
   */
  private getSupersetBaseUrl(): string {
    const url = environment.supersetUrl || '';
    // Remove any path after the domain
    const match = url.match(/^(https?:\/\/[^\/]+)/);
    return match ? match[0] : url;
  }

  /**
   * Initialize the Superset API service by authenticating
   * Call this method when your component initializes to ensure
   * authentication is ready before making API calls
   * @returns Promise that resolves when initialization is complete
   */
  async initialize(): Promise<void> {
    if (!this.isTokenValid()) {
      try {
        await this.accessLogin();
      } catch (error) {
        throw error;
      }
    }
  }

  /**
   * Get query details by primary key
   * @param pk The primary key (ID) of the query to retrieve
   * @returns Observable containing query details including results_key
   */
  getQueryDetails(pk: string | number): Observable<any> {
    const url = `${this.baseUrl}/api/v1/query/${pk}`;

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers => {
        return this.http.get<any>(url, { headers })
          .pipe(
            catchError(error => {
              // If we get a 401 Unauthorized error, try to re-authenticate and retry once
              if (error.status === 401) {
                // Clear existing token to force re-authentication
                this.supersetToken = null;

                return from(this.accessLogin()).pipe(
                  switchMap(() => this.getAuthHeaders()),
                  switchMap(newHeaders => {
                    return this.http.get<any>(url, { headers: newHeaders });
                  })
                );
              }

              // Re-throw the error for other error types
              throw error;
            })
          );
      })
    );
  }

  /**
   * Convert a simple object to a rison format string for Superset API
   * Rison is a format similar to JSON but more compact for URLs
   * @param obj The object to convert to rison format
   * @returns A rison format string
   */
  private toRisonFormat(obj: Record<string, any>): string {
    // For the results key API, we use a specific format that is guaranteed to work
    // This is the most reliable format for Superset API calls
    if (obj && ('key' in obj || 'results_key' in obj)) {
      const keyName = 'key' in obj ? 'key' : 'results_key';
      const keyValue = obj[keyName];
      return `(${keyName}:'${keyValue}'${obj.rows ? ',rows:' + obj.rows : ''})`;
    }

    // For other objects, create a properly formatted Rison string
    const pairs = Object.entries(obj).map(([key, value]) => {
      if (value === null || value === undefined) {
        return `${key}:null`;
      } else if (typeof value === 'string') {
        return `${key}:'${value.replace(/'/g, "\\'")}'`;
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        return `${key}:${value}`;
      } else {
        return `${key}:'${String(value)}'`;
      }
    });

    return '(' + pairs.join(',') + ')';
  }
}
